import { Tag, Tooltip, Divider } from 'antd';
import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { EyeOutlined, EditOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { createArchiveUnarchiveActions } from '../../utils/createArchiveUnarchiveActions';
import { useListResourceArchiving } from '../../utils/listResourceArchiving';
import { useAuthContext } from '../../contexts/AuthContext';
import { SupervisionStatus } from '../../utils/constants/tagColors';
import { useErrorMessage } from '../../utils/errorMessage';
import {
  lastNameFilter,
  postalCodeFilter,
  departmentFilter
} from './utils/supervisionsfilters';
import { genericSorter } from '../../utils/sorterMethods';

/**
 * Custom hook for generating columns configuration for a list of supervisions.
 *
 * @param {function} setForceRefresh - A function to set the refresh state of the list.
 * @returns {Array} An array of column configuration objects for the list.
 */
export const useColumns = (setForceRefresh) => {
  const { t } = useTranslation();
  const { dispatchAPI } = useAuthContext();
  const { message } = useErrorMessage();
  const [enums, setEnums] = useState();
  const searchInput = useRef(null);

  const getEnums = async () => {
    try {
      const { data } = await dispatchAPI('GET', { url: '/supervisions/enums' });
      setEnums(data);
    } catch (e) {
      message(e);
    }
  };

  useEffect(() => {
    (async () => {
      await getEnums();
    })();
  }, []);

  const { deleteResource, restoreResource } = useListResourceArchiving(
    setForceRefresh,
    'supervisions/archiving'
  );

  return [
    {
      title: t('supervisions.form.status'),
      key: 'status',
      dataIndex: 'status',
      width: '10%',
      render: (status) =>
        status ? (
          <Tag color={SupervisionStatus[status]}>
            {t(`supervisions.tags.${status}`, {
              defaultValue: status
            })}
          </Tag>
        ) : (
          'N/R'
        ),
      filters: enums?.status?.map((r) => ({
        text: t(`supervisions.tags.${r}`),
        value: r
      })),
      sorter: true
    },
    {
      className: 'title-wrap',
      title: t('supervisions.form.reference'),
      key: 'reference',
      dataIndex: 'reference',
      sorter: true,
      width: '7%'
    },
    {
      className: 'title-wrap',
      title: t('supervisions.form.owner.name'),
      key: 'main_informations.owner.last_name',
      dataIndex: ['main_informations', 'owner', 'last_name'],
      ...lastNameFilter(searchInput, t),
      sorter: (a, b) =>
        genericSorter(a, b, ['main_informations', 'owner', 'last_name']),
      width: '10%'
    },
    {
      className: 'title-wrap',
      title: t('supervisions.form.owner.reference'),
      key: 'main_informations.owner.reference',
      dataIndex: ['main_informations', 'owner', 'reference'],
      sorter: (a, b) =>
        genericSorter(a, b, ['main_informations', 'owner', 'reference'])
    },
    {
      className: 'title-wrap',
      title: t('supervisions.form.address.postal_code'),
      key: 'petsitting_address.address.postal_code',
      dataIndex: ['petsitting_address', 'address', 'postal_code'],
      ...postalCodeFilter(searchInput, t),
      sorter: (a, b) =>
        a.petsitting_address.address.postal_code -
        b.petsitting_address.address.postal_code
    },
    {
      className: 'title-wrap',
      title: t('supervisions.form.department'),
      key: 'petsitting_address.address.state',
      dataIndex: ['petsitting_address', 'address', 'state'],
      width: '10%',
      ...departmentFilter(searchInput, t),
      sorter: (a, b) =>
        genericSorter(a, b, [
          'address',
          'petsitting_address',
          'address',
          'state'
        ])
    },
    {
      title: t('supervisions.form.address.city'),
      key: 'petsitting_address.address.city',
      dataIndex: ['petsitting_address', 'address', 'city'],
      sorter: (a, b) =>
        genericSorter(a, b, [
          'address',
          'petsitting_address',
          'address',
          'city'
        ])
    },
    {
      title: t('supervisions.form.pensioner.name'),
      key: 'pensioners',
      dataIndex: 'pensioners',
      sorter: true,
      render: (pensioners) =>
        pensioners?.map(
          (pensioner) =>
            `${
              pensioner?.civility
                ? t(`supervisions.form.civility.${pensioner?.civility}`)
                : ''
            } ${pensioner?.first_name || ''} ${pensioner?.last_name || ''}`
        )
    },
    {
      title: t('supervisions.form.pensioner.reference'),
      key: 'pensioners.reference',
      dataIndex: 'pensioners',
      render: (pensioners) =>
        pensioners?.map((pensioner) => pensioner?.reference),
      sorter: (a, b) => {
        const pensionerA = a.pensioners.map((p) => p.reference).join('');
        const pensionerB = b.pensioners.map((p) => p.reference).join('');

        return pensionerA.localeCompare(pensionerB);
      }
    },
    {
      className: 'title-wrap',
      title: t('supervisions.form.start_date'),
      key: 'main_informations.start_date',
      dataIndex: ['main_informations', 'start_date'],
      sorter: true,
      render: (start_date) =>
        start_date && dayjs(start_date).format('DD/MM/YYYY')
    },
    {
      className: 'title-wrap',

      title: t('supervisions.form.end_date'),
      key: 'main_informations.end_date',
      dataIndex: ['main_informations', 'end_date'],
      sorter: true,
      render: (end_date) => end_date && dayjs(end_date).format('DD/MM/YYYY')
    },
    {
      key: 'actions',
      align: 'right',
      fixed: 'right',
      width: 100,
      render: (record) => (
        <>
          <Tooltip
            title={t('datatable.column.action.show.hover')}
            placement="left"
          >
            <Link to={{ pathname: `/supervisions/show/${record._id}` }}>
              <EyeOutlined style={{ fontSize: 18 }} />
            </Link>
          </Tooltip>
          <Divider type="vertical" />
          <Tooltip
            title={
              ['REQUEST', 'WAITING'].includes(record?.status)
                ? t('datatable.column.action.edit.hover')
                : t('datatable.column.action.edit.not_editable')
            }
            placement="left"
          >
            <Link
              to={{
                pathname: ['REQUEST', 'WAITING'].includes(record?.status)
                  ? `/supervisions/edit/${record._id}`
                  : ''
              }}
            >
              <EditOutlined style={{ fontSize: 18 }} />
            </Link>
          </Tooltip>
          {createArchiveUnarchiveActions({
            t,
            deleteResource,
            restoreResource,
            record
          })}
        </>
      )
    }
  ];
};
